import { useState, useMemo, useEffect, useRef, Fragment } from "react"
import { motion } from "motion/react"
import { PieChart } from "@/components/mortgage/PieChart"
import { AnimatedNumber } from "@/components/mortgage/AnimatedNumber"
import JsonFormSteps from "@/MyCode/components/JsonFormSteps"
import type { Step } from "@/MyCode/core/types"
import { Button } from "@/components/ui/button"
import { Download } from "lucide-react"
import debounce from "lodash.debounce";
import { useDebounce } from "@/MyCode/hooks/useDebounce"

interface PaymentSchedule {
    month: number
    date: string
    year: number
    payment: number
    principal: number
    interest: number
    balance: number
}

interface YearGroup {
    year: number
    payments: PaymentSchedule[]
    totals: {
        payment: number
        principal: number
        interest: number
        endingBalance: number
    }
}

export function MortgageCalculatorJson() {
    // Get current month and year
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1

    const [loanAmount, setLoanAmount] = useState(300000)
    const [housePrice, setHousePrice] = useState(400000)
    const [downPayment, setDownPayment] = useState(100000)
    const [interestRate, setInterestRate] = useState(4.5)
    const [loanTerm, setLoanTerm] = useState(30)
    const [startMonth, setStartMonth] = useState(currentMonth)
    const [startYear, setStartYear] = useState(currentYear)
    const [propertyTax, setPropertyTax] = useState(3000)
    const [homeInsurance, setHomeInsurance] = useState(1200)
    const [otherCosts, setOtherCosts] = useState(500)
    const [scheduleView, setScheduleView] = useState<Set<string>>(new Set(["months"]))
    const [extraMonthlyPay, setExtraMonthlyPay] = useState(0)
    const [extraMonthlyFromMonth, setExtraMonthlyFromMonth] = useState(currentMonth)
    const [extraMonthlyFromYear, setExtraMonthlyFromYear] = useState(currentYear)
    const [extraYearlyPay, setExtraYearlyPay] = useState(0)
    const [extraYearlyFromMonth, setExtraYearlyFromMonth] = useState(currentMonth)
    const [extraYearlyFromYear, setExtraYearlyFromYear] = useState(currentYear)
    const [extraOneTimePay, setExtraOneTimePay] = useState(0)

    // Debounced values for form inputs (500ms delay)
    const debouncedLoanAmount = useDebounce(loanAmount, 500)
    const debouncedDownPayment = useDebounce(downPayment, 500)
    const debouncedInterestRate = useDebounce(interestRate, 500)
    const debouncedLoanTerm = useDebounce(loanTerm, 500)
    const debouncedPropertyTax = useDebounce(propertyTax, 500)
    const debouncedHomeInsurance = useDebounce(homeInsurance, 500)
    const debouncedOtherCosts = useDebounce(otherCosts, 500)
    const debouncedExtraMonthlyPay = useDebounce(extraMonthlyPay, 500)
    const debouncedExtraYearlyPay = useDebounce(extraYearlyPay, 500)
    const debouncedExtraOneTimePay = useDebounce(extraOneTimePay, 500)

    // Calculate loan amount based on house price and down payment
    // If down payment is provided (including 0), loan amount = house price - down payment
    // Otherwise use the manually entered loan amount
    // Use current values (not debounced) for immediate updates, but use debounced for calculations
    const effectiveLoanAmount = useMemo(() => {
        // If down payment has been set (even if 0), use it to calculate loan amount
        // Check both current and debounced values to handle immediate updates
        const currentDownPayment = downPayment !== undefined && downPayment !== null ? downPayment : debouncedDownPayment
        if (currentDownPayment !== undefined && currentDownPayment !== null && housePrice > 0) {
            return Math.max(0, housePrice - currentDownPayment)
        }
        return debouncedLoanAmount
    }, [housePrice, downPayment, debouncedDownPayment, debouncedLoanAmount])

    // Calculate the actual down payment to display
    // Prefer the user-entered down payment value, otherwise calculate from house price and loan amount
    // Use current value for immediate display updates
    const displayDownPayment = useMemo(() => {
        // Check current value first for immediate updates, then fall back to debounced
        const currentDownPayment = downPayment !== undefined && downPayment !== null ? downPayment : debouncedDownPayment
        if (currentDownPayment !== undefined && currentDownPayment !== null) {
            return currentDownPayment
        }
        return Math.max(0, housePrice - effectiveLoanAmount)
    }, [downPayment, debouncedDownPayment, housePrice, effectiveLoanAmount])

    // Generate year options (current year ± 50 years)
    const yearOptions = Array.from({ length: 101 }, (_, i) => currentYear - 50 + i)

    // Month names
    const monthNames = [
        // "January", "February", "March", "April", "May", "June",
        // "July", "August", "September", "October", "November", "December"
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ]

    const calculateMortgage = useMemo(() => {
        const principal = effectiveLoanAmount
        const monthlyRate = debouncedInterestRate / 100 / 12
        const numberOfPayments = debouncedLoanTerm * 12

        if (monthlyRate === 0) {
            const monthlyPayment = principal / numberOfPayments
            return {
                monthlyPayment,
                totalPayment: monthlyPayment * numberOfPayments,
                totalInterest: 0,
            }
        }

        const monthlyPayment =
            (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
            (Math.pow(1 + monthlyRate, numberOfPayments) - 1)

        const totalPayment = monthlyPayment * numberOfPayments
        const totalInterest = totalPayment - principal

        return {
            monthlyPayment,
            totalPayment,
            totalInterest,
        }
    }, [effectiveLoanAmount, debouncedInterestRate, debouncedLoanTerm])

    const paymentSchedule: PaymentSchedule[] = useMemo(() => {
        const schedule: PaymentSchedule[] = []
        const monthlyRate = debouncedInterestRate / 100 / 12
        const numberOfPayments = debouncedLoanTerm * 12
        let balance = effectiveLoanAmount

        // Helper function to format date and get year
        const formatDate = (monthOffset: number) => {
            let year = startYear
            let month = startMonth + monthOffset - 1

            // Adjust year if month exceeds 12
            while (month > 12) {
                month -= 12
                year += 1
            }

            const date = new Date(year, month - 1, 1)
            return {
                formatted: date.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
                year: year,
                month: month,
            }
        }

        // Helper function to check if extra monthly payment applies
        const shouldApplyExtraMonthly = (monthOffset: number) => {
            const dateInfo = formatDate(monthOffset)
            const startDate = new Date(extraMonthlyFromYear, extraMonthlyFromMonth - 1, 1)
            const currentDate = new Date(dateInfo.year, dateInfo.month - 1, 1)
            return currentDate >= startDate && debouncedExtraMonthlyPay > 0
        }

        // Helper function to check if extra yearly payment applies
        const shouldApplyExtraYearly = (monthOffset: number) => {
            const dateInfo = formatDate(monthOffset)
            const startDate = new Date(extraYearlyFromYear, extraYearlyFromMonth - 1, 1)
            const currentDate = new Date(dateInfo.year, dateInfo.month - 1, 1)
            // Apply yearly payment in the same month each year
            if (currentDate >= startDate && debouncedExtraYearlyPay > 0) {
                const monthsSinceStart = (dateInfo.year - extraYearlyFromYear) * 12 + (dateInfo.month - extraYearlyFromMonth)
                return monthsSinceStart >= 0 && monthsSinceStart % 12 === 0
            }
            return false
        }

        // Apply one-time payment at the start
        if (debouncedExtraOneTimePay > 0) {
            balance = Math.max(0, balance - debouncedExtraOneTimePay)
        }

        if (monthlyRate === 0) {
            const monthlyPayment = balance / numberOfPayments
            for (let month = 1; month <= numberOfPayments && balance > 0.01; month++) {
                const interest = 0
                let principal = monthlyPayment
                let extraPrincipal = 0

                // Apply extra monthly payment
                if (shouldApplyExtraMonthly(month)) {
                    extraPrincipal += debouncedExtraMonthlyPay
                }

                // Apply extra yearly payment
                if (shouldApplyExtraYearly(month)) {
                    extraPrincipal += debouncedExtraYearlyPay
                }

                principal += extraPrincipal
                balance -= principal
                const dateInfo = formatDate(month)
                schedule.push({
                    month,
                    date: dateInfo.formatted,
                    year: dateInfo.year,
                    payment: monthlyPayment + extraPrincipal,
                    principal,
                    interest,
                    balance: Math.max(0, balance),
                })
            }
        } else {
            const monthlyPayment = calculateMortgage.monthlyPayment
            for (let month = 1; month <= numberOfPayments && balance > 0.01; month++) {
                const interest = balance * monthlyRate
                let principal = monthlyPayment - interest
                let extraPrincipal = 0

                // Apply extra monthly payment
                if (shouldApplyExtraMonthly(month)) {
                    extraPrincipal += debouncedExtraMonthlyPay
                }

                // Apply extra yearly payment
                if (shouldApplyExtraYearly(month)) {
                    extraPrincipal += debouncedExtraYearlyPay
                }

                principal += extraPrincipal
                balance -= principal
                const dateInfo = formatDate(month)
                schedule.push({
                    month,
                    date: dateInfo.formatted,
                    year: dateInfo.year,
                    payment: monthlyPayment + extraPrincipal,
                    principal,
                    interest,
                    balance: Math.max(0, balance),
                })
            }
        }

        return schedule
    }, [
        effectiveLoanAmount,
        debouncedInterestRate,
        debouncedLoanTerm,
        calculateMortgage.monthlyPayment,
        startMonth,
        startYear,
        debouncedExtraMonthlyPay,
        extraMonthlyFromMonth,
        extraMonthlyFromYear,
        debouncedExtraYearlyPay,
        extraYearlyFromMonth,
        extraYearlyFromYear,
        debouncedExtraOneTimePay,
    ])

    // Group payments by year and calculate totals
    const yearGroups: YearGroup[] = useMemo(() => {
        const groups: { [year: number]: PaymentSchedule[] } = {}

        paymentSchedule.forEach((payment) => {
            if (!groups[payment.year]) {
                groups[payment.year] = []
            }
            groups[payment.year].push(payment)
        })

        return Object.keys(groups)
            .sort((a, b) => Number(a) - Number(b))
            .map((year) => {
                const payments = groups[Number(year)]
                const totals = payments.reduce(
                    (acc, payment) => ({
                        payment: acc.payment + payment.payment,
                        principal: acc.principal + payment.principal,
                        interest: acc.interest + payment.interest,
                        endingBalance: payment.balance, // Last payment's balance
                    }),
                    { payment: 0, principal: 0, interest: 0, endingBalance: 0 }
                )
                return {
                    year: Number(year),
                    payments,
                    totals,
                }
            })
    }, [paymentSchedule])

    const monthlyTax = debouncedPropertyTax / 12
    const monthlyInsurance = debouncedHomeInsurance / 12
    const monthlyOther = debouncedOtherCosts / 12

    // Calculate first month's principal and interest breakdown
    const monthlyPrincipal = useMemo(() => {
        const monthlyRate = debouncedInterestRate / 100 / 12
        if (monthlyRate === 0) {
            return calculateMortgage.monthlyPayment
        }
        // First month interest = loan amount * monthly rate
        const firstMonthInterest = effectiveLoanAmount * monthlyRate
        // First month principal = monthly payment - interest
        return calculateMortgage.monthlyPayment - firstMonthInterest
    }, [calculateMortgage.monthlyPayment, effectiveLoanAmount, debouncedInterestRate])

    const monthlyInterest = useMemo(() => {
        const monthlyRate = debouncedInterestRate / 100 / 12
        if (monthlyRate === 0) {
            return 0
        }
        return effectiveLoanAmount * monthlyRate
    }, [effectiveLoanAmount, debouncedInterestRate])

    const totalMonthlyPayment =
        calculateMortgage.monthlyPayment +
        monthlyTax +
        monthlyInsurance +
        monthlyOther +
        debouncedExtraMonthlyPay

    // Calculate scenario without extra payments for comparison
    const scenarioWithoutExtraPayments = useMemo(() => {
        const monthlyRate = debouncedInterestRate / 100 / 12
        const numberOfPayments = debouncedLoanTerm * 12
        let balance = effectiveLoanAmount
        let totalInterest = 0
        let totalPayments = 0

        if (monthlyRate === 0) {
            const monthlyPayment = balance / numberOfPayments
            totalPayments = monthlyPayment * numberOfPayments
            totalInterest = 0
        } else {
            const monthlyPayment = calculateMortgage.monthlyPayment
            for (let month = 1; month <= numberOfPayments && balance > 0.01; month++) {
                const interest = balance * monthlyRate
                const principal = monthlyPayment - interest
                balance -= principal
                totalInterest += interest
                totalPayments += monthlyPayment
            }
        }

        return {
            totalPayments,
            totalInterest,
            payoffDate: new Date(startYear, startMonth - 1 + numberOfPayments, 1),
            numberOfPayments,
        }
    }, [effectiveLoanAmount, debouncedInterestRate, debouncedLoanTerm, calculateMortgage.monthlyPayment, startYear, startMonth])

    // Calculate totals with extra payments
    const totalsWithExtraPayments = useMemo(() => {
        const totalPayments = paymentSchedule.reduce((sum, p) => sum + p.payment, 0)
        const totalInterest = paymentSchedule.reduce((sum, p) => sum + p.interest, 0)

        // Calculate total extra payments - sum of all extra principal payments
        const totalExtraPayments = paymentSchedule.reduce((sum, p) => {
            const regularPayment = calculateMortgage.monthlyPayment
            const extraPrincipal = p.payment - regularPayment
            return sum + Math.max(0, extraPrincipal)
        }, 0) + debouncedExtraOneTimePay

        // Calculate payoff date from last payment
        let payoffDateCalculated: Date | null = null
        const lastPayment = paymentSchedule[paymentSchedule.length - 1]
        if (lastPayment) {
            const dateParts = lastPayment.date.split(' ')
            const monthName = dateParts[0]
            const year = parseInt(dateParts[1])
            const monthIndex = monthNames.findIndex(m => m === monthName) + 1
            if (monthIndex > 0) {
                payoffDateCalculated = new Date(year, monthIndex - 1, 1)
            }
        }

        return {
            totalPayments,
            totalInterest,
            totalExtraPayments,
            numberOfPayments: paymentSchedule.length,
            payoffDate: payoffDateCalculated,
        }
    }, [paymentSchedule, calculateMortgage.monthlyPayment, debouncedExtraOneTimePay, monthNames])

    // Calculate savings
    const savings = useMemo(() => {
        const interestSaved = scenarioWithoutExtraPayments.totalInterest - totalsWithExtraPayments.totalInterest
        const monthsReduced = scenarioWithoutExtraPayments.numberOfPayments - totalsWithExtraPayments.numberOfPayments
        const yearsReduced = Math.floor(monthsReduced / 12)
        const remainingMonths = monthsReduced % 12

        return {
            interestSaved,
            monthsReduced,
            yearsReduced,
            remainingMonths,
        }
    }, [scenarioWithoutExtraPayments, totalsWithExtraPayments])

    // Calculate total dollar amount over the life of the loan
    // Include extra payments in the calculation
    const totalDollarAmount = useMemo(() => {
        return totalsWithExtraPayments.totalPayments + monthlyTax * totalsWithExtraPayments.numberOfPayments +
            monthlyInsurance * totalsWithExtraPayments.numberOfPayments +
            monthlyOther * totalsWithExtraPayments.numberOfPayments
    }, [totalsWithExtraPayments, monthlyTax, monthlyInsurance, monthlyOther])

    // Track changes to trigger border/shadow animation
    const [highlightTrigger, setHighlightTrigger] = useState(0)
    const prevValuesRef = useRef({
        monthlyPayment: calculateMortgage.monthlyPayment,
        monthlyPrincipal,
        monthlyInterest,
        monthlyTax,
        monthlyInsurance,
        monthlyOther,
        totalMonthlyPayment,
    })


    // Generate month options for select fields
    const monthOptions = monthNames.map((month, index) => ({
        label: month,
        value: String(index + 1)
    }))

    // Generate year options for select fields
    const yearSelectOptions = yearOptions.map(year => ({
        label: String(year),
        value: String(year)
    }))

    // Helper function to update state from form data
    const updateStateFromFormData = (data: any) => {
        if (data.housePrice !== undefined) setHousePrice(Number(data.housePrice))

        // Priority: If downPayment is being changed, use it to calculate loanAmount
        // Only recalculate loanAmount from downPayment if downPayment was actually changed
        if (data.downPayment !== undefined) {
            const newDownPayment = Number(data.downPayment)
            setDownPayment(newDownPayment)
            // If down payment is provided (even if 0), calculate loan amount
            if (housePrice > 0) {
                setLoanAmount(Math.max(0, housePrice - newDownPayment))
            }
        } else if (data.loanAmount !== undefined) {
            // Only recalculate downPayment from loanAmount if downPayment wasn't changed
            const newLoanAmount = Number(data.loanAmount)
            setLoanAmount(newLoanAmount)
            // If loan amount is manually set, calculate down payment
            if (housePrice > 0 && newLoanAmount > 0) {
                setDownPayment(Math.max(0, housePrice - newLoanAmount))
            }
        }
        if (data.interestRate !== undefined) setInterestRate(Number(data.interestRate))
        if (data.loanTerm !== undefined) setLoanTerm(Number(data.loanTerm))
        if (data.startMonth !== undefined) setStartMonth(Number(data.startMonth))
        if (data.startYear !== undefined) setStartYear(Number(data.startYear))
        if (data.propertyTax !== undefined) setPropertyTax(Number(data.propertyTax))
        if (data.homeInsurance !== undefined) setHomeInsurance(Number(data.homeInsurance))
        if (data.otherCosts !== undefined) setOtherCosts(Number(data.otherCosts))
        if (data.extraMonthlyPay !== undefined) setExtraMonthlyPay(Number(data.extraMonthlyPay))
        if (data.extraMonthlyFromMonth !== undefined) setExtraMonthlyFromMonth(Number(data.extraMonthlyFromMonth))
        if (data.extraMonthlyFromYear !== undefined) setExtraMonthlyFromYear(Number(data.extraMonthlyFromYear))
        if (data.extraYearlyPay !== undefined) setExtraYearlyPay(Number(data.extraYearlyPay))
        if (data.extraYearlyFromMonth !== undefined) setExtraYearlyFromMonth(Number(data.extraYearlyFromMonth))
        if (data.extraYearlyFromYear !== undefined) setExtraYearlyFromYear(Number(data.extraYearlyFromYear))
        if (data.extraOneTimePay !== undefined) setExtraOneTimePay(Number(data.extraOneTimePay))
    }

    const steps: Step[] = [
        {
            id: "loan-details",
            title: "Loan Details",
            description: "Enter your mortgage loan information",
            fields: [
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "housePrice",
                    label: "House Price 111",
                    placeholder: "400000",
                    required: true,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,  // Optional: default is false
                    incrementValues: [1000, 10000, 100000],  // Optional: default is [100, 10, 1]
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        housePrice: 'required|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "downPayment",
                    label: "Down Payment",
                    placeholder: "100000",
                    required: false,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,  // Optional: default is false
                    incrementValues: [1000, 10000, 100000],  // Optional: default is [100, 10, 1]                    ui: { colSpan: 6 },
                    laravelValidationRules: {
                        downPayment: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "loanAmount",
                    label: "Loan Amount",
                    placeholder: "300000",
                    required: true,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,  // Optional: default is false
                    incrementValues: [1000, 10000, 100000],  // Optional: default is [100, 10, 1]                        ui: { colSpan: 6 },
                    laravelValidationRules: {
                        loanAmount: 'required|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "interestRate",
                    label: "Interest Rate (%)",
                    placeholder: "4.5",
                    required: true,
                    minValue: 0,
                    maxValue: 100,
                    allowNegative: false,  // Optional: default is false
                    incrementValues: [1, .1, .01],  // Optional: default is [100, 10, 1]    
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        interestRate: 'required|numeric|min:0|max:100'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "loanTerm",
                    label: "Loan Term (years)",
                    placeholder: "30",
                    required: true,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,  // Optional: default is false
                    incrementValues: [1, 5],  // Optional: default is [100, 10, 1]    
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        loanTerm: 'required|integer|min:1|max:50'
                    }
                },
                {
                    type: "select",
                    name: "startMonth",
                    label: "Start Month",
                    required: true,
                    options: {
                        source: "static" as const,
                        items: monthOptions
                    },
                    ui: { colSpan: 6 },
                    laravelValidationRules: {
                        startMonth: 'required|string|in:1,2,3,4,5,6,7,8,9,10,11,12'
                    }
                },
                {
                    type: "select",
                    name: "startYear",
                    label: "Start Year",
                    required: true,
                    options: {
                        source: "static" as const,
                        items: yearSelectOptions
                    },
                    ui: { colSpan: 6 },
                    laravelValidationRules: {
                        startYear: 'required|string'
                    }
                }
            ]
        },
        {
            id: "additional-costs",
            title: "Additional Costs",
            description: "Enter annual costs for taxes, insurance, and other expenses",
            fields: [
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "propertyTax",
                    label: "Property Tax (annual)",
                    placeholder: "3000",
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,  // Optional: default is false
                    incrementValues: [10, 100, 1000],  // Optional: default is [100, 10, 1]                          ui: { colSpan: 12 },
                    laravelValidationRules: {
                        propertyTax: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "homeInsurance",
                    label: "Home Insurance (annual)",
                    placeholder: "1200",
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,  // Optional: default is false
                    incrementValues: [10, 100, 1000],  // Optional: default is [100, 10, 1]                        ui: { colSpan: 12 },
                    laravelValidationRules: {
                        homeInsurance: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "otherCosts",
                    label: "Other Costs (annual)",
                    placeholder: "500",
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,  // Optional: default is false
                    incrementValues: [10, 100, 1000],  // Optional: default is [100, 10, 1]                        ui: { colSpan: 12 },
                    laravelValidationRules: {
                        otherCosts: 'nullable|numeric|min:0'
                    }
                }
            ]
        },
        {
            id: "extra-payments",
            title: "Extra Payments",
            description: "Optional extra payments to reduce your loan balance faster",
            fields: [
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "extraMonthlyPay",
                    label: "Extra Monthly Pay",
                    placeholder: "0",
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,  // Optional: default is false
                    incrementValues: [10, 100, 1000],  // Optional: default is [100, 10, 1]                            ui: { colSpan: 12 },
                    laravelValidationRules: {
                        extraMonthlyPay: 'nullable|numeric|min:0'
                    },
                    hidden: true
                },
                // {
                //     type: "select",
                //     name: "extraMonthlyFromMonth",
                //     label: "Extra Monthly From Month",
                //     options: {
                //         source: "static" as const,
                //         items: monthOptions
                //     },
                //     ui: { colSpan: 6 },
                //     // visibleWhen: [
                //     //     { field: "extraMonthlyPay", op: ">" as const, value: 0 }
                //     // ],
                //     hidden: true,
                //     laravelValidationRules: {
                //         extraMonthlyFromMonth: 'nullable|string|in:1,2,3,4,5,6,7,8,9,10,11,12'
                //     }
                // },
                // {
                //     type: "select",
                //     name: "extraMonthlyFromYear",
                //     label: "Extra Monthly From Year",
                //     options: {
                //         source: "static" as const,
                //         items: yearSelectOptions
                //     },
                //     ui: { colSpan: 6 },
                //     // visibleWhen: [
                //     //     { field: "extraMonthlyPay", op: ">" as const, value: 0 }
                //     // ],
                //     hidden: true,
                //     laravelValidationRules: {
                //         extraMonthlyFromYear: 'nullable|string'
                //     }
                // },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "extraYearlyPay",
                    label: "Extra Yearly Pay",
                    placeholder: "0",
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,  // Optional: default is false
                    incrementValues: [10, 100, 1000],  // Optional: default is [100, 10, 1]                            ui: { colSpan: 12 },
                    laravelValidationRules: {
                        extraYearlyPay: 'nullable|numeric|min:0'
                    }
                },
                // {
                //     type: "select",
                //     name: "extraYearlyFromMonth",
                //     label: "Extra Yearly From Month",
                //     options: {
                //         source: "static" as const,
                //         items: monthOptions
                //     },
                //     ui: { colSpan: 6 },
                //     visibleWhen: [
                //         { field: "extraYearlyPay", op: ">" as const, value: 0 }
                //     ],
                //     laravelValidationRules: {
                //         extraYearlyFromMonth: 'nullable|string|in:1,2,3,4,5,6,7,8,9,10,11,12'
                //     }
                // },
                // {
                //     type: "select",
                //     name: "extraYearlyFromYear",
                //     label: "Extra Yearly From Year",
                //     options: {
                //         source: "static" as const,
                //         items: yearSelectOptions
                //     },
                //     ui: { colSpan: 6 },
                //     visibleWhen: [
                //         { field: "extraYearlyPay", op: ">" as const, value: 0 }
                //     ],
                //     laravelValidationRules: {
                //         extraYearlyFromYear: 'nullable|string'
                //     }
                // },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "extraOneTimePay",
                    label: "Extra One-time Pay",
                    placeholder: "0",
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,  // Optional: default is false
                    incrementValues: [10, 100, 1000],  // Optional: default is [100, 10, 1]                            ui: { colSpan: 12 },
                    laravelValidationRules: {
                        extraOneTimePay: 'nullable|numeric|min:0'
                    }
                }
            ]
        }
    ]

    useEffect(() => {
        const currentValues = {
            monthlyPayment: calculateMortgage.monthlyPayment,
            monthlyPrincipal,
            monthlyInterest,
            monthlyTax,
            monthlyInsurance,
            monthlyOther,
            totalMonthlyPayment,
        }

        // Check if any value changed significantly
        const hasChanged = Object.keys(currentValues).some(
            (key) =>
                Math.abs(
                    currentValues[key as keyof typeof currentValues] -
                    prevValuesRef.current[key as keyof typeof prevValuesRef.current]
                ) > 0.01
        )

        if (hasChanged) {
            setHighlightTrigger((prev) => prev + 1)
            prevValuesRef.current = currentValues
        }
    }, [
        calculateMortgage.monthlyPayment,
        monthlyPrincipal,
        monthlyInterest,
        monthlyTax,
        monthlyInsurance,
        monthlyOther,
        totalMonthlyPayment,
    ])

    const pieChartData = useMemo(() => [
        { label: "Principal", value: Math.round(monthlyPrincipal), color: "#3b82f6", },
        { label: "Interest", value: Math.round(monthlyInterest), color: "#60a5fa", },
        { label: "Taxes", value: Math.round(monthlyTax), color: "#10b981", },
        { label: "Insurance", value: Math.round(monthlyInsurance), color: "#f59e0b", },
        { label: "Other Costs", value: Math.round(monthlyOther), color: "#ef4444", },
        ...(debouncedExtraMonthlyPay > 0 ? [{ label: "Extra Monthly Pay", value: Math.round(debouncedExtraMonthlyPay), color: "#8b5cf6", }] : []),
    ], [monthlyPrincipal, monthlyInterest, monthlyTax, monthlyInsurance, monthlyOther, debouncedExtraMonthlyPay])

    // Calculate total breakdown pie chart data
    const totalBreakdownPieChartData = useMemo(() => {
        // Principal paid from regular payments = Total Payments - Total Interest - Total Extra Payments
        const principalPaid = totalsWithExtraPayments.totalPayments - totalsWithExtraPayments.totalInterest - totalsWithExtraPayments.totalExtraPayments
        // Use the actual down payment value (from state or calculated)
        const actualDownPayment = displayDownPayment

        return [
            { label: "Down Payment", value: actualDownPayment, color: "#10b981", },
            { label: "Principal Paid", value: principalPaid, color: "#3b82f6", },
            { label: "Total Interest", value: totalsWithExtraPayments.totalInterest, color: "#ef4444", },
            ...(totalsWithExtraPayments.totalExtraPayments > 0 ? [{ label: "Extra Payments", value: totalsWithExtraPayments.totalExtraPayments, color: "#8b5cf6", }] : []),
        ]
    }, [displayDownPayment, totalsWithExtraPayments])

    // Download payment schedule as CSV
    const downloadPaymentSchedule = () => {
        const csvRows: string[] = []

        // CSV Header
        csvRows.push("Date,Payment,Principal,Interest,Balance")

        if (scheduleView.has("months")) {
            // Monthly view - export all months
            paymentSchedule.forEach((payment) => {
                csvRows.push(
                    `"${payment.date}",${payment.payment.toFixed(2)},${payment.principal.toFixed(2)},${payment.interest.toFixed(2)},${payment.balance.toFixed(2)}`
                )
            })
        } else {
            // Yearly view - export year totals
            yearGroups.forEach((yearGroup) => {
                csvRows.push(
                    `"${yearGroup.year}",${yearGroup.totals.payment.toFixed(2)},${yearGroup.totals.principal.toFixed(2)},${yearGroup.totals.interest.toFixed(2)},${yearGroup.totals.endingBalance.toFixed(2)}`
                )
            })
        }

        // Create and download file
        const csvContent = csvRows.join("\n")
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
        const link = document.createElement("a")
        const url = URL.createObjectURL(blob)
        link.setAttribute("href", url)
        link.setAttribute("download", `mortgage-payment-schedule-${new Date().toISOString().split("T")[0]}.csv`)
        link.style.visibility = "hidden"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
        >
            <div>
                <h1 className="text-3xl font-bold mb-2">Mortgage Calculator</h1>
                <p className="text-muted-foreground">
                    Calculate your monthly mortgage payment and view payment schedule
                </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                {/* Left Side - Form */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-2 h-full"
                >
                    <JsonFormSteps
                        divClassName="w-full p-0 h-full"
                        hideSubmitButton={true}
                        steps={steps}
                        onSubmit={(data: any) => {
                            // Update state from form data on submit
                            updateStateFromFormData(data)
                        }}
                        // onChange={(data: any) => {
                        //     // Update state from form data on change
                        //     updateStateFromFormData(data)
                        // }}
                        onChange={useMemo(
                            () =>
                                debounce((data: any) => {
                                    updateStateFromFormData(data);
                                }, 500),
                            [updateStateFromFormData]
                        )}
                        debug={false}
                        debugSingleForm={true}
                        progressBarPosition="top"
                        noCard={false}
                        horizontalLayout={true}
                        initialValues={{
                            housePrice: housePrice,
                            downPayment: downPayment,
                            loanAmount: loanAmount,
                            interestRate: interestRate,
                            loanTerm: loanTerm,
                            startMonth: String(startMonth),
                            startYear: String(startYear),
                            propertyTax: propertyTax,
                            homeInsurance: homeInsurance,
                            otherCosts: otherCosts,
                            extraMonthlyPay: extraMonthlyPay,
                            extraMonthlyFromMonth: String(extraMonthlyFromMonth),
                            extraMonthlyFromYear: String(extraMonthlyFromYear),
                            extraYearlyPay: extraYearlyPay,
                            extraYearlyFromMonth: String(extraYearlyFromMonth),
                            extraYearlyFromYear: String(extraYearlyFromYear),
                            extraOneTimePay: extraOneTimePay,
                        }}
                        resetOnInitialValuesChange={false}
                    />
                </motion.div>

                {/* Right Side - Pie Chart */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-1 space-y-4"
                >
                    {/* Pie Chart */}
                    <div className="bg-card border border-border rounded-lg p-4 gap-4 flex flex-col">
                        <h3 className="text-lg font-semibold mb-4 text-center">Total Cost Breakdown</h3>
                        <PieChart data={totalBreakdownPieChartData} size={300} />

                    </div>
                    <div className="bg-card border border-border rounded-lg p-4">
                        <div className="text-sm text-muted-foreground mb-1">Monthly Payment</div>
                        <div className="text-2xl font-bold">
                            <AnimatedNumber value={totalMonthlyPayment} prefix="$" decimals={2} />
                        </div>
                    </div>

                    <div className="bg-card border border-border rounded-lg p-4">
                        <div className="text-sm text-muted-foreground mb-1">Total Interest</div>
                        <div className="text-2xl font-bold mb-1">
                            <AnimatedNumber value={totalsWithExtraPayments.totalInterest} prefix="$" decimals={2} />
                        </div>
                        <div className="text-sm text-muted-foreground mb-1">Total Payments</div>
                        <div className="text-2xl font-bold">
                            <AnimatedNumber value={totalsWithExtraPayments.totalPayments} prefix="$" decimals={2} />
                        </div>
                    </div>

                    <div className="bg-card border border-border rounded-lg p-4">
                        <div className="text-sm text-muted-foreground mb-1">Payoff Time</div>
                        <div className="text-2xl font-bold">
                            {totalsWithExtraPayments.payoffDate ? (
                                totalsWithExtraPayments.payoffDate.toLocaleDateString("en-US", { month: "short", year: "numeric" })
                            ) : (
                                `${Math.floor(totalsWithExtraPayments.numberOfPayments / 12)} years ${totalsWithExtraPayments.numberOfPayments % 12} months`
                            )}
                        </div>
                    </div>

                    {/* Savings Section */}
                    {totalsWithExtraPayments.totalExtraPayments > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6, duration: 0.3 }}
                            className="bg-card border-2 border-primary/20 rounded-lg p-4"
                        >
                            <h3 className="text-lg font-semibold mb-4 text-primary">Savings with Extra Payments</h3>
                            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                                <div>
                                    <div className="text-sm text-muted-foreground mb-1">Time Reduced:</div>
                                    <div className="text-xl font-bold text-primary">
                                        {savings.yearsReduced > 0 && `${savings.yearsReduced} year${savings.yearsReduced > 1 ? 's' : ''} `}
                                        {savings.remainingMonths > 0 && `${savings.remainingMonths} month${savings.remainingMonths > 1 ? 's' : ''}`}
                                        {savings.yearsReduced === 0 && savings.remainingMonths === 0 && '0 months'}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-muted-foreground mb-1">Interest Saved:</div>
                                    <div className="text-xl font-bold text-primary">
                                        $<AnimatedNumber value={savings.interestSaved} decimals={0} />
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </motion.div>
            </div>

            {/* Detailed Breakdown Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Monthly Breakdown */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.3 }}
                    className="bg-card border border-border rounded-lg p-4"
                >
                    <h3 className="text-lg font-semibold mb-4">Monthly Breakdown</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Principal:</span>
                            <span className="text-sm font-semibold">
                                $<AnimatedNumber value={monthlyPrincipal} decimals={2} />
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Interest:</span>
                            <span className="text-sm font-semibold">
                                $<AnimatedNumber value={monthlyInterest} decimals={2} />
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Taxes:</span>
                            <span className="text-sm font-semibold">
                                $<AnimatedNumber value={monthlyTax} decimals={2} />
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Insurance:</span>
                            <span className="text-sm font-semibold">
                                $<AnimatedNumber value={monthlyInsurance} decimals={2} />
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Other Costs:</span>
                            <span className="text-sm font-semibold">
                                $<AnimatedNumber value={monthlyOther} decimals={2} />
                            </span>
                        </div>
                        {debouncedExtraMonthlyPay > 0 && (
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Extra Monthly Pay:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={debouncedExtraMonthlyPay} decimals={2} />
                                </span>
                            </div>
                        )}
                        <div className="pt-3 border-t border-border flex justify-between">
                            <span className="text-base font-semibold">Total Monthly:</span>
                            <span className="text-base font-bold text-primary">
                                $<AnimatedNumber value={totalMonthlyPayment} decimals={2} />
                            </span>
                        </div>
                    </div>
                </motion.div>

                {/* Loan Details */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.3 }}
                    className="bg-card border border-border rounded-lg p-4"
                >
                    <h3 className="text-lg font-semibold mb-4">Loan Details</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">House Price:</span>
                            <span className="text-sm font-semibold">
                                $<AnimatedNumber value={housePrice} decimals={0} />
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Loan Amount:</span>
                            <span className="text-sm font-semibold">
                                $<AnimatedNumber value={effectiveLoanAmount} decimals={0} />
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Down Payment:</span>
                            <span className="text-sm font-semibold">
                                $<AnimatedNumber value={displayDownPayment} decimals={0} />
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">
                                Total of {totalsWithExtraPayments.numberOfPayments} Mortgage Payments:
                            </span>
                            <span className="text-sm font-semibold">
                                $<AnimatedNumber value={totalsWithExtraPayments.totalPayments} decimals={0} />
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Total Interest:</span>
                            <span className="text-sm font-semibold">
                                $<AnimatedNumber value={totalsWithExtraPayments.totalInterest} decimals={0} />
                            </span>
                        </div>
                        {totalsWithExtraPayments.totalExtraPayments > 0 && (
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Total Extra Payments:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={totalsWithExtraPayments.totalExtraPayments} decimals={0} />
                                </span>
                            </div>
                        )}
                        {totalsWithExtraPayments.payoffDate && (
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Mortgage Payoff Date:</span>
                                <span className="text-sm font-semibold">
                                    {totalsWithExtraPayments.payoffDate.toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                                </span>
                            </div>
                        )}
                        <div className="pt-3 border-t border-border flex justify-between">
                            <span className="text-base font-bold">Total Amount:</span>
                            <span className="text-base font-bold text-primary">
                                $<AnimatedNumber value={totalDollarAmount} decimals={0} />
                            </span>
                        </div>
                    </div>
                </motion.div>
            </div>


            {/* Payment Schedule Table */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                className="mt-6"
            >
                <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-2xl font-bold">Payment Schedule</h2>
                        <div className="flex items-center gap-3">
                            <div className="w-auto">
                                <JsonFormSteps
                                    steps={[
                                        {
                                            id: "schedule-view",
                                            title: "",
                                            description: "",
                                            fields: [
                                                {
                                                    type: "checkSelectBox",
                                                    name: "scheduleView",
                                                    label: "",
                                                    options: {
                                                        source: "static" as const,
                                                        items: [
                                                            { value: "months", title: "Months", label: "Months", icon: "CalendarDays" },
                                                            { value: "years", title: "Years", label: "Years", icon: "Calendar" },
                                                        ],
                                                    },
                                                    ui: { colSpan: 12, gridCols: 2, multiSelect: false },
                                                },
                                            ],
                                        },
                                    ]}
                                    onSubmit={() => { }}
                                    onChange={(data) => {
                                        // Convert string value to Set<string>
                                        const value = data.scheduleView || "months"
                                        setScheduleView(new Set([value]))
                                    }}
                                    hideSubmitButton={true}
                                    noCard={true}
                                    divClassName="w-auto"
                                    initialValues={{
                                        scheduleView: Array.from(scheduleView)[0] || "months",
                                    }}
                                    resetOnInitialValuesChange={false}
                                />
                            </div>
                            <Button
                                onClick={downloadPaymentSchedule}
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-2"
                            >
                                <Download size={16} />
                                Download
                            </Button>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-muted">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold">Payment</th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold">Principal</th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold">Interest</th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold">Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {scheduleView.has("months") ? (
                                    // Monthly view - show all months grouped by year
                                    yearGroups.map((yearGroup) => (
                                        <Fragment key={`year-group-${yearGroup.year}`}>
                                            {/* Year Header */}
                                            <tr className="bg-primary/10 border-t-2 border-primary/20">
                                                <td colSpan={5} className="px-4 py-3 font-bold text-primary">
                                                    {yearGroup.year}
                                                </td>
                                            </tr>
                                            {/* Payments for this year */}
                                            {yearGroup.payments.map((row) => (
                                                <tr
                                                    key={row.month}
                                                    className="border-t border-border hover:bg-muted/50"
                                                >
                                                    <td className="px-4 py-2 text-sm">{row.date}</td>
                                                    <td className="px-4 py-2 text-sm text-right">
                                                        ${row.payment.toLocaleString(undefined, {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        })}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-right text-green-600">
                                                        ${row.principal.toLocaleString(undefined, {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        })}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-right text-red-600">
                                                        ${row.interest.toLocaleString(undefined, {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        })}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm text-right font-medium">
                                                        ${row.balance.toLocaleString(undefined, {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        })}
                                                    </td>
                                                </tr>
                                            ))}
                                            {/* Year Totals */}
                                            <tr className="bg-muted border-t-2 border-border font-semibold">
                                                <td className="px-4 py-2 text-sm">Year {yearGroup.year} Total</td>
                                                <td className="px-4 py-2 text-sm text-right">
                                                    ${yearGroup.totals.payment.toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-right text-green-600">
                                                    ${yearGroup.totals.principal.toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-right text-red-600">
                                                    ${yearGroup.totals.interest.toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-right font-medium">
                                                    ${yearGroup.totals.endingBalance.toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </td>
                                            </tr>
                                        </Fragment>
                                    ))
                                ) : (
                                    // Yearly view - show only year totals (cumulative values)
                                    yearGroups.map((yearGroup) => (
                                        <tr
                                            key={`year-${yearGroup.year}`}
                                            className="border-t border-border hover:bg-muted/50"
                                        >
                                            <td className="px-4 py-2 text-sm font-medium">{yearGroup.year}</td>
                                            <td className="px-4 py-2 text-sm text-right">
                                                ${yearGroup.totals.payment.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-right text-green-600">
                                                ${yearGroup.totals.principal.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-right text-red-600">
                                                ${yearGroup.totals.interest.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-right font-medium">
                                                ${yearGroup.totals.endingBalance.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    )
}
